/**
 * Create Project Dialog
 *
 * Form dialog for creating new projects with:
 * - Free text: client, project name, product, activities
 * - Controlled: therapeutic area (cascades to indications), team roles
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CreateProjectRequest } from '../types';
import { useReferenceData } from '../hooks/useReferenceData';

interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: CreateProjectRequest) => Promise<void>;
}

const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const { referenceData, loading: refDataLoading } = useReferenceData();

  // Form state
  const [formData, setFormData] = useState<Partial<CreateProjectRequest>>({
    client: '',
    projectName: '',
    description: '',
    product: '',
    therapeuticArea: '',
    indication: '',
    activity: '',
    subActivity: '',
    projectStart: new Date().toISOString(),
    team: [],
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter indications based on selected therapeutic area
  const availableIndications = React.useMemo(() => {
    if (!referenceData || !formData.therapeuticArea) return [];
    return referenceData.indications.filter(
      (ind) => ind.therapeuticArea === formData.therapeuticArea
    );
  }, [referenceData, formData.therapeuticArea]);

  // Reset indication when therapeutic area changes
  useEffect(() => {
    if (formData.therapeuticArea) {
      const indicationStillValid = availableIndications.some(
        (ind) => ind.code === formData.indication
      );
      if (!indicationStillValid) {
        setFormData((prev) => ({ ...prev, indication: '' }));
      }
    }
  }, [formData.therapeuticArea, availableIndications, formData.indication]);

  const handleChange = (field: keyof CreateProjectRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.client?.trim()) {
      setError('Client name is required');
      return;
    }
    if (!formData.projectName?.trim()) {
      setError('Project name is required');
      return;
    }
    if (!formData.description?.trim()) {
      setError('Description is required');
      return;
    }
    if (!formData.therapeuticArea) {
      setError('Therapeutic area is required');
      return;
    }
    if (!formData.activity?.trim()) {
      setError('Activity is required');
      return;
    }
    if (!formData.projectStart) {
      setError('Project start date is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit(formData as CreateProjectRequest);
      // Reset form
      setFormData({
        client: '',
        projectName: '',
        description: '',
        product: '',
        therapeuticArea: '',
        indication: '',
        activity: '',
        subActivity: '',
        projectStart: new Date().toISOString(),
        team: [],
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError(null);
      onClose();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          {refDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Grid container spacing={2}>
                {/* Basic Info */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    label="Client"
                    placeholder="Enter client name (free text)"
                    value={formData.client}
                    onChange={(e) => handleChange('client', e.target.value)}
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Product"
                    placeholder="Enter product name (optional, free text)"
                    value={formData.product}
                    onChange={(e) => handleChange('product', e.target.value)}
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    required
                    label="Project Name"
                    placeholder="e.g., CTA - Initial CTA, IND Submission - FDA Review"
                    value={formData.projectName}
                    onChange={(e) => handleChange('projectName', e.target.value)}
                    disabled={submitting}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={3}
                    label="Description"
                    placeholder="Enter project description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    disabled={submitting}
                  />
                </Grid>

                {/* Clinical Classification */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Clinical Classification
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="Therapeutic Area"
                    value={formData.therapeuticArea}
                    onChange={(e) => handleChange('therapeuticArea', e.target.value)}
                    disabled={submitting || !referenceData}
                    helperText="Select therapeutic area first"
                  >
                    {(referenceData?.therapeuticAreas || []).map((ta) => (
                      <MenuItem key={ta.code} value={ta.code}>
                        {ta.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Indication"
                    value={formData.indication}
                    onChange={(e) => handleChange('indication', e.target.value)}
                    disabled={submitting || !formData.therapeuticArea || availableIndications.length === 0}
                    helperText={
                      !formData.therapeuticArea
                        ? 'Select therapeutic area first'
                        : availableIndications.length === 0
                        ? 'No indications available'
                        : 'Optional - filtered by therapeutic area'
                    }
                  >
                    {availableIndications.map((ind) => (
                      <MenuItem key={ind.code} value={ind.code}>
                        {ind.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Project Details */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Project Details
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    label="Activity"
                    placeholder="Enter activity type (free text)"
                    value={formData.activity}
                    onChange={(e) => handleChange('activity', e.target.value)}
                    disabled={submitting}
                    helperText="E.g., CTA, IND Submission, etc."
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Sub-Activity"
                    placeholder="Enter sub-activity (optional, free text)"
                    value={formData.subActivity}
                    onChange={(e) => handleChange('subActivity', e.target.value)}
                    disabled={submitting}
                    helperText="E.g., IRAS support, Document review, etc."
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DatePicker
                    label="Project Start Date"
                    value={formData.projectStart ? new Date(formData.projectStart) : null}
                    onChange={(date) =>
                      handleChange('projectStart', date ? date.toISOString() : '')
                    }
                    disabled={submitting}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Note: You can add team members and log contributions after creating the project.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || refDataLoading}
          >
            {submitting ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateProjectDialog;
